import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "../cn";
import { Button } from "./Button";

export interface WizardStep {
  key: string;
  label: string;
}

/** Sticky progress rail for multi-step flows. */
export function WizardProgress({
  steps,
  currentIndex,
  onStepClick,
  className,
  compact,
}: {
  steps: WizardStep[];
  currentIndex: number;
  onStepClick?: (index: number) => void;
  className?: string;
  /** Dots instead of labelled steps (mobile). */
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className={cn("flex items-center justify-center gap-2", className)} aria-label="Progress">
        {steps.map((s, i) => (
          <span
            key={s.key}
            className={cn(
              "h-1.5 rounded-full transition-all",
              i === currentIndex ? "w-6 bg-brand" : i < currentIndex ? "w-1.5 bg-brand/50" : "w-1.5 bg-line",
            )}
          />
        ))}
      </div>
    );
  }
  return (
    <ol className={cn("flex items-center gap-1 overflow-x-auto", className)}>
      {steps.map((s, i) => {
        const state = i < currentIndex ? "complete" : i === currentIndex ? "current" : "upcoming";
        return (
          <li key={s.key} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              disabled={!onStepClick || i > currentIndex}
              onClick={() => onStepClick?.(i)}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm",
                state === "current" ? "font-medium text-ink" : "text-mute",
                onStepClick && i <= currentIndex && "hover:bg-subtle",
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
                  state === "complete" && "bg-ok text-white",
                  state === "current" && "bg-brand text-white",
                  state === "upcoming" && "border border-line bg-card text-faint",
                )}
              >
                {state === "complete" ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s.label}
            </button>
            {i < steps.length - 1 && <span className="h-px flex-1 bg-line" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

/** Standard wizard footer with Back / secondary / Continue. */
export function WizardFooter({
  onBack,
  onContinue,
  continueLabel = "Continue",
  backLabel = "Back",
  secondary,
  continueDisabled,
  showBack = true,
  className,
}: {
  onBack?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  backLabel?: string;
  secondary?: React.ReactNode;
  continueDisabled?: boolean;
  showBack?: boolean;
  className?: string;
}) {
  // flex-wrap on both rows: a long continueLabel/secondary combination (an
  // approval-gate step pairs "Submit to approval gate" with a "Save and
  // finish later" ghost button, alongside Back) has no room on one
  // non-wrapping row once the wizard is at phone width - wrapping to a
  // second row degrades gracefully instead of clipping/overflowing.
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-2 border-t border-line bg-card px-6 py-4", className)}>
      <div>
        {showBack && (
          <Button variant="ghost" onClick={onBack}>
            {backLabel}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {secondary}
        <Button onClick={onContinue} disabled={continueDisabled}>
          {continueLabel}
        </Button>
      </div>
    </div>
  );
}
